import { Outlet, useLocation } from 'react-router-dom'
import { AccessDenied } from '@/components/common/AccessDenied'
import { getPermissionForPath } from '@/config/routePermissions'
import { usePermissions } from '@/hooks/usePermissions'
import { Spinner } from '@/components/ui/Spinner'

/**
 * Access is based only on GET /users/me (data.roleId.permissions). /settings is always allowed (profile/security).
 * Other paths require can(module, action ?? 'read'). Anything else shows AccessDenied.
 */
export function PermissionGuard(): React.ReactElement {
  const location = useLocation()
  const path = location.pathname
  const { can, canAny, hasModule, isLoading } = usePermissions()

  const isAllowed = (perm: {
    module: string;
    action?: string;
    actions?: string[];
    allOf?: Array<{ module: string; action?: string; actions?: string[] }>;
  }): boolean => {
    if (perm.allOf && perm.allOf.length > 0) return perm.allOf.every(isAllowed)
    if (perm.actions && perm.actions.length > 0) return canAny(perm.module, perm.actions)
    if (perm.action !== undefined) return can(perm.module, perm.action)
    return hasModule(perm.module)
  }
  const permission = getPermissionForPath(path)

  if (isLoading) {
    return (
      <div className="kc-loader-in flex flex-1 items-center justify-center p-8">
        <Spinner label="Opening page" />
      </div>
    )
  }

  if (path === '/settings' || path.startsWith('/settings/')) {
    const settingsPermission = getPermissionForPath(path)
    if (settingsPermission) {
      if (!isAllowed(settingsPermission)) return <AccessDenied />
    }
    return <Outlet />
  }

  if (permission) {
    if (!isAllowed(permission)) return <AccessDenied />
  }

  return <Outlet />
}
